"use client";

import { useEffect, useState } from "react";

/**
 * Starts the MSW browser worker whenever mocking is enabled
 * (NEXT_PUBLIC_API_MOCKING=enabled). Renders nothing until the worker is ready
 * so the first data fetch is intercepted.
 *
 * Gated purely on the mocking flag — NOT on NODE_ENV — because this app also
 * ships as a mock-backed *production* build (no real backend): without the
 * browser worker, every client-side react-query fetch escapes to
 * NEXT_PUBLIC_BACKEND (an unreachable localhost) and fails. This mirrors the
 * server-side MSW gate in instrumentation.ts. Never set the flag on a build
 * that talks to a real backend, or MSW will hijack live traffic.
 */
export function MswProvider({ children }: { children: React.ReactNode }) {
  const mockingEnabled = process.env.NEXT_PUBLIC_API_MOCKING === "enabled";
  const [ready, setReady] = useState(!mockingEnabled);

  useEffect(() => {
    if (!mockingEnabled) return;
    let active = true;
    void import("./browser").then(async ({ startWorker }) => {
      await startWorker();
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [mockingEnabled]);

  if (!ready) return null;
  return <>{children}</>;
}
